import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import IntlCurrencyInput from "react-intl-currency-input";
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "calc(100% - 32px)",
  maxWidth: 560,
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: "0 20px 50px rgba(15,23,42,0.18)",
  p: "32px 24px 28px",
  outline: "none",
};



const iconHaloStyle = {
  width: 86,
  height: 86,
  borderRadius: "50%",
  bgcolor: "#f1f4ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mx: "auto",
  mb: 2,
};

const iconStyle = {
  width: 52,
  height: 52,
  borderRadius: "50%",
  bgcolor: "#ff3100",
  color: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 12px 24px #ff3100",
};

const oldValueBoxStyle = {
  borderRadius: "10px",
  bgcolor: "#f7f9ff",
  border: "1px solid #edf1fb",
  px: 2,
  py: 1.5,
};

const inputGridStyle = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "1fr 1fr",
  },
  gap: 1.5,
};

const actionGridStyle = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "1fr 1fr",
  },
  gap: 1.5,
};

function parseCurrencyValue(value) {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;

  const normalized = String(value)
    .trim()
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function formatCurrency(value) {
  const parsed = parseCurrencyValue(value);
  if (!Number.isFinite(parsed)) return "";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsed);
}

function formatCurrencyInput(value) {
  return formatCurrency(value).replace(/\u00a0/g, " ");
}

export function calculateTonValueFromGrossValue(grossValue, tonQuantity) {
  const gross = parseCurrencyValue(grossValue);
  const quantity = parseCurrencyValue(tonQuantity);

  if (!Number.isFinite(gross) || !Number.isFinite(quantity) || quantity <= 0) {
    return NaN;
  }

  return Number((gross / quantity).toFixed(2));
}

export function calculateGrossValueFromTonValue(
  tonValue,
  tonQuantity,
  referenceGrossValue,
) {
  const value = parseCurrencyValue(tonValue);
  const quantity = parseCurrencyValue(tonQuantity);

  if (!Number.isFinite(value) || !Number.isFinite(quantity) || quantity <= 0) {
    return NaN;
  }

  const calculatedGrossValue = Number((value * quantity).toFixed(2));
  const referenceGross = parseCurrencyValue(referenceGrossValue);

  if (
    Number.isFinite(referenceGross) &&
    Math.abs(calculatedGrossValue - referenceGross) <= 0.02
  ) {
    return referenceGross;
  }

  return calculatedGrossValue;
}

function isDeleteKey(key) {
  return key === "Backspace" || key === "Delete";
}

function hasNumber(value) {
  return /\d/.test(String(value ?? ""));
}

export default function FreightEditemodSuccessModaTest({
  open,
  valueByTon,
  handleClose,
  onResult,
  calculateGrossFreightProposedByShipper,
  dataMsg,
  title = "Atualizar valor bruto",
}) {
  const oldValue = useMemo(
    () =>
      parseCurrencyValue(
        dataMsg?.oldValue ?? dataMsg?.value ?? dataMsg?.total ?? 0,
      ),
    [dataMsg],
  );
  const byTheTon = useMemo(
    () => parseCurrencyValue(dataMsg?.byTheTon),
    [dataMsg],
  );
  const tonQuantity = useMemo(
    () => parseCurrencyValue(valueByTon),
    [valueByTon],
  );
  const [grossValue, setGrossValue] = useState("");
  const [tonValue, setTonValue] = useState("");
  const [error, setError] = useState("");
  const grossDeleteKeyRef = useRef(false);
  const tonDeleteKeyRef = useRef(false);

  const currencyConfig = {
    locale: "pt-BR",
    formats: {
      number: {
        BRL: {
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      },
    },
  };

  const currencyConfigKm = {
    locale: "pt-BR",
    formats: {
      number: {
        BRL: {
          //   style: "Km",
          currency: "BRL",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      },
    },
  };


  useEffect(() => {
    if (!open) return;
    setGrossValue(Number.isFinite(oldValue) ? formatCurrencyInput(oldValue) : "");
    setTonValue(
      Number.isFinite(byTheTon)
        ? formatCurrencyInput(byTheTon)
        : Number.isFinite(oldValue) &&
          Number.isFinite(tonQuantity) &&
          tonQuantity > 0
          ? formatCurrencyInput(oldValue / tonQuantity)
          : "",
    );
    setError("");
  }, [byTheTon, open, oldValue, tonQuantity]);

  function clearEditableValues() {
    setGrossValue("");
    setTonValue("");
    setError("");
  }

  function handleGrossValueChange(event, value, maskedValue) {
    const nextDisplayValue = maskedValue ?? event.target.value;
    const nextGrossValue = Number.isFinite(value)
      ? value
      : parseCurrencyValue(nextDisplayValue);

    if (
      grossDeleteKeyRef.current &&
      Number.isFinite(nextGrossValue) &&
      nextGrossValue >= 0 &&
      nextGrossValue < 1
    ) {
      grossDeleteKeyRef.current = false;
      clearEditableValues();
      return;
    }
    grossDeleteKeyRef.current = false;

    if (!hasNumber(nextDisplayValue)) {
      clearEditableValues();
      return;
    }

    setGrossValue(nextDisplayValue);
    if (
      Number.isFinite(nextGrossValue) &&
      Number.isFinite(tonQuantity) &&
      tonQuantity > 0
    ) {
      setTonValue(
        formatCurrencyInput(
          calculateTonValueFromGrossValue(nextGrossValue, tonQuantity),
        ),
      );
    }
    setError("");
  }

  function handleTonValueChange(event, value, maskedValue) {
    const nextDisplayValue = maskedValue ?? event.target.value;
    const nextTonValue = Number.isFinite(value)
      ? value
      : parseCurrencyValue(nextDisplayValue);

    if (
      tonDeleteKeyRef.current &&
      Number.isFinite(nextTonValue) &&
      nextTonValue >= 0 &&
      nextTonValue < 1
    ) {
      tonDeleteKeyRef.current = false;
      clearEditableValues();
      return;
    }
    tonDeleteKeyRef.current = false;

    if (!hasNumber(nextDisplayValue)) {
      clearEditableValues();
      return;
    }

    setTonValue(nextDisplayValue);
    if (
      Number.isFinite(nextTonValue) &&
      Number.isFinite(tonQuantity) &&
      tonQuantity > 0
    ) {
      setGrossValue(
        formatCurrencyInput(
          calculateGrossValueFromTonValue(
            nextTonValue,
            tonQuantity,
            grossValue || oldValue,
          ),
        ),
      );
    }
    setError("");
  }

  function handleGrossValueBlur() {
    const parsedValue = parseCurrencyValue(grossValue);
    setGrossValue(Number.isFinite(parsedValue) ? formatCurrencyInput(parsedValue) : "");
  }

  function handleTonValueBlur() {
    const parsedValue = parseCurrencyValue(tonValue);
    setTonValue(Number.isFinite(parsedValue) ? formatCurrencyInput(parsedValue) : "");
  }

  function handleResidualGrossValueDelete(event) {
    grossDeleteKeyRef.current = isDeleteKey(event.key);
    if (!grossDeleteKeyRef.current) return;

    const currentValue = parseCurrencyValue(event.currentTarget.value);
    if (Number.isFinite(currentValue) && currentValue >= 0 && currentValue < 1) {
      event.preventDefault();
      grossDeleteKeyRef.current = false;
      clearEditableValues();
    }
  }

  function handleResidualTonValueDelete(event) {
    tonDeleteKeyRef.current = isDeleteKey(event.key);
    if (!tonDeleteKeyRef.current) return;

    const currentValue = parseCurrencyValue(event.currentTarget.value);
    if (Number.isFinite(currentValue) && currentValue >= 0 && currentValue < 1) {
      event.preventDefault();
      tonDeleteKeyRef.current = false;
      clearEditableValues();
    }
  }

  function handleConfirmClick() {
    const newValue = parseCurrencyValue(grossValue);
    if (!Number.isFinite(newValue) || newValue < 0) {
      setError("Informe um valor bruto válido.");
      return;
    }

    calculateGrossFreightProposedByShipper(newValue);

    onResult?.({
      oldValue: Number.isFinite(oldValue) ? oldValue : 0,
      newValue,
      formattedOldValue: formatCurrency(oldValue),
      formattedNewValue: formatCurrency(newValue),
    });
    handleClose?.();
  }

  return (
    <Modal
      keepMounted
      open={open}
      onClose={handleClose}
      aria-labelledby="gross-value-modal-title"
      aria-describedby="gross-value-modal-description"
    >
      <Box sx={modalStyle}>
        <Box sx={iconHaloStyle}>
          <Box sx={iconStyle}>
            <CheckRoundedIcon />
          </Box>
        </Box>

        <Typography
          id="gross-value-modal-title"
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#111827",
            textAlign: "center",
            mb: 1,
          }}
        >
          {title}
        </Typography>

        <Typography
          id="gross-value-modal-description"
          sx={{
            color: "#4b5563",
            fontSize: 13,
            lineHeight: 1.45,
            textAlign: "center",
            mb: 2.5,
          }}
        >
          Informe o novo valor bruto. O valor antigo fica visível para
          conferência antes de confirmar.
        </Typography>

        <Stack spacing={2}>
          <Box sx={oldValueBoxStyle}>
            <Typography sx={{ color: "#6b7280", fontSize: 12, mb: 0.5 }}>
              Valor bruto antigo.
            </Typography>
            <Typography sx={{ color: "#111827", fontWeight: 700 }}>
              {formatCurrency(oldValue)}
            </Typography>
          </Box>

          {Number.isFinite(byTheTon) && (
            <Box sx={oldValueBoxStyle}>
              <Typography sx={{ color: "#6b7280", fontSize: 12, mb: 0.5 }}>
                Valor por tonelada antigo.
              </Typography>
              <Typography sx={{ color: "#111827", fontWeight: 700 }}>
                {formatCurrency(byTheTon)}/Ton
              </Typography>
            </Box>
          )}

          <Box sx={inputGridStyle}>


            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                Novo valor bruto
              </Typography>

              <IntlCurrencyInput
                currency="BRL"
                config={currencyConfig}
                placeholder="00.00"
                value={grossValue}
                onChange={handleGrossValueChange}
                onBlur={handleGrossValueBlur}
                onKeyDown={handleResidualGrossValueDelete}
                className="form-control"
                maxLength={15}
                style={{ borderRadius: "10px" }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                Novo valor por tonelada
              </Typography>

              <IntlCurrencyInput
                currency="BRL"
                config={currencyConfig}
                placeholder="00.00"
                value={tonValue}
                onChange={handleTonValueChange}
                onBlur={handleTonValueBlur}
                onKeyDown={handleResidualTonValueDelete}
                className="form-control"
                maxLength={15}
                style={{ borderRadius: "10px" }}
              />
            </Box>


          </Box>

          <Box sx={actionGridStyle}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClose}
              sx={{
                borderColor: "#d1d5db",
                color: "#374151",
                borderRadius: "8px",
                py: 1.25,
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#9ca3af",
                  bgcolor: "#f9fafb",
                  boxShadow: "none",
                },
              }}
            >
              Cancelar
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={handleConfirmClick}
              sx={{
                bgcolor: "#f6f8ff",
                color: "#111827",
                borderRadius: "8px",
                py: 1.25,
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#edf2ff",
                  boxShadow: "none",
                },
              }}
            >
              Confirmar novo valor
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
}
